import { ChangeEvent, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { SaveIndicator, useSaveStatus } from "@/components/ui/save-indicator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form } from "@/components/ui/form";
import { AddressFields } from "@/components/forms/AddressFields";
import { composeAddressLine, emptyAddressFields, normalizeAddressFields } from "@/lib/address";
import { US_STATES } from "@/lib/usStates";
import { EmergencyContactModal } from "@/components/forms/EmergencyContactModal";
import { Label } from "@/components/ui/label";
import { formatDateDisplay, formatSSN, maskSSN, capitalizeFirst } from "@/lib/utils";
import { SsnInput } from "@/components/employees/SsnInput";
import { fetchFullSsns, ssnToSave } from "@/lib/ssnReveal";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { formatPhoneInputValue } from "@/lib/phone";
import { useLocation } from "wouter";
import { DependentDeletionDialog } from "@/components/employee/DependentDeletionDialog";
import { DependentQuickAddForm } from "@/components/dependents/DependentQuickAddForm";
import type { EmergencyContact } from "@shared/schema";
import { DEPENDENT_RELATIONSHIP_OPTIONS } from "@shared/constants/dependentRelationships";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Edit,
  Save,
  Plus,
  Trash2,
  Shield,
  Users,
  FileText,
  AlertTriangle,
  Calendar,
  Heart,
  X,
  Eye,
  EyeOff
} from "lucide-react";

interface Dependent {
  id?: number;
  firstName: string;
  lastName: string;
  relationship: string;
  dateOfBirth: string;
  ssn?: string;
  sex?: string;
  isStudent?: boolean;
}

// Profile validation schema
const profileSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(100, "First name is too long"),
  lastName: z.string().min(1, "Last name is required").max(100, "Last name is too long"),
  personalEmail: z.string().email("Invalid email address").or(z.literal("")),
  // Masked ***-**-1234 = unchanged value from the API (not sent on save)
  ssn: z.string().regex(/^(\d{3}-?\d{2}-?\d{4})?$|^\*{3}-\*{2}-[\d*]{4}$/, "Invalid SSN format").or(z.literal("")),
  dateOfBirth: z.string().optional(),
  sex: z.string().optional(),
  maritalStatus: z.string().optional(),
  phoneNumber: z.string().regex(/^(\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})?$/, "Invalid phone format").or(z.literal("")),
  address1: z.string().max(200, "Address is too long").optional(),
  address2: z.string().max(200, "Address is too long").optional(),
  city: z.string().max(100, "City name is too long").optional(),
  state: z.string().optional(),
  zipCode: z.string().regex(/^(\d{5}(-\d{4})?)?$/, "Invalid ZIP code format").or(z.literal("")).optional(),
  county: z.string().max(100, "County name is too long").optional(),
  country: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

function parseServerErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) {
    return fallback;
  }

  const message = error.message ?? "";
  const separatorIndex = message.indexOf(":");
  const detail =
    separatorIndex >= 0 ? message.slice(separatorIndex + 1).trim() : message;

  try {
    const parsed = JSON.parse(detail);
    if (typeof parsed?.message === "string" && parsed.message.trim().length) {
      return parsed.message;
    }
  } catch {
    // If the detail was not JSON, fall through to use the raw text.
  }

  if (detail.length > 0 && !/^\d{3}$/.test(detail)) {
    return detail;
  }

  return fallback;
}

export default function Profile() {
  const [isEditing, setIsEditing] = useState(false);
  const [showSSN, setShowSSN] = useState(false);
  // Full SSN fetched on demand (API rows carry ***-**-1234); undefined = not revealed.
  const [revealedSsn, setRevealedSsn] = useState<string | null | undefined>(undefined);
  const ssnRevealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toggleSSNReveal = async () => {
    if (!showSSN && revealedSsn === undefined && employeeRecord?.id) {
      try {
        setRevealedSsn((await fetchFullSsns(employeeRecord.id, "view")).ssn);
      } catch {
        return;
      }
    }
    if (ssnRevealTimerRef.current) clearTimeout(ssnRevealTimerRef.current);
    setShowSSN((prev) => {
      const next = !prev;
      if (next) {
        ssnRevealTimerRef.current = setTimeout(() => setShowSSN(false), 8000);
      }
      return next;
    });
  };
  useEffect(() => {
    return () => {
      if (ssnRevealTimerRef.current) clearTimeout(ssnRevealTimerRef.current);
    };
  }, []);
  const [showDependentDialog, setShowDependentDialog] = useState(false);
  const [showEmergencyContactDialog, setShowEmergencyContactDialog] = useState(false);
  const [editingDependent, setEditingDependent] = useState<Dependent | null>(null);
  // Same, for the dependent in the Edit Dependent dialog.
  const [revealedDependentSsn, setRevealedDependentSsn] = useState<string | null | undefined>(undefined);
  const [editingEmergencyContact, setEditingEmergencyContact] = useState<EmergencyContact | null>(null);
  const [pendingDeletionDependent, setPendingDeletionDependent] = useState<Dependent | null>(null);
  const [deleteDependentError, setDeleteDependentError] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(() => {
    // Check if banner was previously dismissed
    return document.cookie.split('; ').find(row => row.startsWith('profileBannerDismissed=')) === undefined;
  });
  const pendingDeletionName = useMemo(() => {
    if (!pendingDeletionDependent) {
      return "";
    }
    const first = pendingDeletionDependent.firstName?.trim() ?? "";
    const last = pendingDeletionDependent.lastName?.trim() ?? "";
    const full = `${first} ${last}`.trim();
    return full.length > 0 ? full : pendingDeletionDependent.relationship || "Dependent";
  }, [pendingDeletionDependent]);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  // Authenticated user (still fetched for potential UI, but we now rely on session self endpoints)
  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/user"] });
  // const clientId = user?.clientId as number | undefined; // Reserved for future scoping if needed client-side

  // Employee record fetch (scoped)
  const { data: employeeRecord } = useQuery<any>({
    queryKey: ["/api/employees/me"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/employees/me");
      return response.json();
    },
  });

  // Initialize react-hook-form with validation
  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      personalEmail: "",
      ssn: "",
      dateOfBirth: "",
      sex: "",
      maritalStatus: "",
      phoneNumber: "",
      ...emptyAddressFields(),
    },
    mode: "onChange", // Validate on change for real-time feedback
  });

  const { control, handleSubmit, reset, getValues, formState: { errors } } = form;

  // Save status for auto-save indicator
  const saveStatus = useSaveStatus();

  // Debounce timer ref for auto-save
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedDataRef = useRef<string | null>(null);

  // Watch all form values for auto-save
  const watchedValues = useWatch({ control });

  // Reset form with employee data when loaded (skip while editing to avoid overwriting user input)
  useEffect(() => {
    if (!employeeRecord || isEditing) {
      return;
    }

    setRevealedSsn(undefined);
    setShowSSN(false);
    const addressDefaults = normalizeAddressFields(employeeRecord);

    reset({
      firstName: employeeRecord.firstName || "",
      lastName: employeeRecord.lastName || "",
      personalEmail: employeeRecord.personalEmail || "",
      ssn: employeeRecord.ssn || "",
      dateOfBirth: employeeRecord.dateOfBirth || "",
      sex: employeeRecord.sex || "",
      maritalStatus: employeeRecord.maritalStatus || "",
      phoneNumber: employeeRecord.phone || "",
      ...addressDefaults,
    });
    // Update the last saved data reference when data loads
    lastSavedDataRef.current = JSON.stringify({
      firstName: employeeRecord.firstName || "",
      lastName: employeeRecord.lastName || "",
      personalEmail: employeeRecord.personalEmail || "",
      ssn: employeeRecord.ssn || "",
      dateOfBirth: employeeRecord.dateOfBirth || "",
      sex: employeeRecord.sex || "",
      maritalStatus: employeeRecord.maritalStatus || "",
      phoneNumber: employeeRecord.phone || "",
      ...addressDefaults,
    });
  }, [employeeRecord, reset, isEditing]);

  // Auto-save effect - triggers when form values change in edit mode
  useEffect(() => {
    // Only auto-save when in edit mode
    if (!isEditing || !employeeRecord) {
      return;
    }

    // Check if values have actually changed from the last saved state
    const currentDataStr = JSON.stringify(watchedValues);
    if (currentDataStr === lastSavedDataRef.current) {
      return;
    }

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new debounced auto-save timer (500ms)
    autoSaveTimerRef.current = setTimeout(async () => {
      // Validate required fields before auto-save
      const data = getValues();
      if (!data.firstName?.trim() || !data.lastName?.trim()) {
        // Don't auto-save if required fields are empty
        return;
      }

      // Check for validation errors before auto-save
      const isValid = await form.trigger();
      if (!isValid) {
        // Don't auto-save if there are validation errors
        return;
      }

      // Trigger auto-save
      handleAutoSave(data);
    }, 500);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [watchedValues, isEditing, employeeRecord]);

  // Auto-save handler (without exiting edit mode)
  const handleAutoSave = useCallback((data: any) => {
    const addressLine = composeAddressLine(data);

    const payload: any = {
      firstName: data.firstName,
      lastName: data.lastName,
      personalEmail: data.personalEmail,
      ssn: data.ssn || null,
      dateOfBirth: data.dateOfBirth || null,
      sex: data.sex || null,
      maritalStatus: data.maritalStatus || null,
      phone: data.phoneNumber || null,
      address: addressLine || null,
      address1: data.address1 || null,
      address2: data.address2 || null,
      city: data.city || null,
      state: data.state || null,
      zipCode: data.zipCode || null,
      county: data.county || null,
      country: data.country || null,
    };
    // Unchanged (masked or same as revealed) SSN is not sent, so it can't overwrite the stored one.
    if (ssnToSave(payload.ssn, revealedSsn) === undefined) delete payload.ssn;

    // Compute field-level diffs for history
    const changes: Record<string, { from: any; to: any }> = {};
    const fieldsToTrack = [
      'firstName', 'lastName', 'personalEmail', 'phone', 'dateOfBirth',
      'sex', 'maritalStatus', 'ssn',
      'address1', 'address2', 'city', 'state', 'zipCode', 'county', 'country'
    ];
    fieldsToTrack.forEach((field) => {
      if (field === 'ssn' && !('ssn' in payload)) return;
      const oldVal = (employeeRecord as any)?.[field];
      const newVal = field === 'phone' ? payload.phone : payload[field];
      if (oldVal !== newVal && (oldVal || newVal)) {
        changes[field] = { from: oldVal ?? 'N/A', to: newVal ?? 'N/A' };
      }
    });

    // Only save if there are actual changes
    if (Object.keys(changes).length === 0) {
      return;
    }

    (window as any).__profileChanges = changes;
    saveStatus.setSaving();
    // Sent SSN becomes the new "unchanged" baseline for later auto-saves
    if ('ssn' in payload) setRevealedSsn(payload.ssn);
    autoSaveMutation.mutate(payload);
  }, [employeeRecord, revealedSsn]);

  // New dependent form state
  const [newDependent, setNewDependent] = useState<Dependent>({
    firstName: "",
    lastName: "",
    relationship: "",
    dateOfBirth: "",
    ssn: "",
    sex: "",
    isStudent: false
  });

  // Dependents (scoped)
  const { data: dependentsRaw = [], isLoading: dependentsLoading } = useQuery({
    queryKey: ["/api/employees/me/dependents"],
    queryFn: async () => {
      try {
        const response = await apiRequest("GET", "/api/employees/me/dependents");
        return response.json();
      } catch {
        const response = await apiRequest("GET", "/api/employees/dependents"); // fallback
        return response.json();
      }
    }
  });
  const dependents: Dependent[] = Array.isArray(dependentsRaw)
    ? (dependentsRaw as Dependent[])
    : [];

  // Emergency Contacts (scoped)
  const { data: emergencyContactsRaw = [], isLoading: emergencyLoading } = useQuery({
    queryKey: ["/api/employees/me/emergency-contacts"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/employees/me/emergency-contacts");
      return response.json();
    },
  });
  const emergencyContacts: EmergencyContact[] = Array.isArray(emergencyContactsRaw) ? emergencyContactsRaw : [];

  // Add dependent mutation
  const addDependentMutation = useMutation({
    mutationFn: async (dependentData: any) => {
      try {
        const response = await apiRequest("POST", "/api/employees/me/dependents", dependentData);
        return response.json();
      } catch {
        const response = await apiRequest("POST", "/api/employees/dependents", dependentData);
        return response.json();
      }
    },
    onSuccess: async (created: any) => {
      const dependentName = `${newDependent.firstName} ${newDependent.lastName}`;

      toast({
        title: "Dependent Added",
        description: `${dependentName} has been added successfully.`,
        variant: "success",
      });
      
      queryClient.invalidateQueries({ queryKey: ["/api/employees/me/dependents"] });
      setNewDependent({
        firstName: "",
        lastName: "",
        relationship: "",
        dateOfBirth: "",
        ssn: "",
        sex: "",
        isStudent: false
      });
      setShowDependentDialog(false);
      
      // Log to history
      try {
        if (employeeRecord?.id) {
          await apiRequest("POST", `/api/employees/${employeeRecord.id}/history`, {
            action: "dependent_added",
            description: `Added dependent: ${dependentName}`,
            recordType: "dependent",
            recordId: created?.id ?? null,
          });
        }
      } catch (err) {
        console.error("Failed to create dependent history:", err);
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add dependent. Please try again.",
        variant: "destructive",
      });
    }
  });

  // Update dependent mutation
  const updateDependentMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      return apiRequest("PATCH", `/api/employees/me/dependents/${id}`, data);
    },
    onSuccess: () => {
      toast({
        title: "Dependent Updated",
        description: "Dependent information has been updated successfully.",
        variant: "success",
      });
  queryClient.invalidateQueries({ queryKey: ["/api/employees/me/dependents"] });
      setEditingDependent(null);
      setShowDependentDialog(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update dependent. Please try again.",
        variant: "destructive",
      });
    }
  });

  // Delete dependent mutation
  const deleteDependentMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/employees/me/dependents/${id}`);
    },
    onSuccess: () => {
      const dependentLabel =
        pendingDeletionName.length > 0 ? pendingDeletionName : "Dependent";
      toast({
        title: "Dependent Deleted",
        description: `${dependentLabel} has been removed successfully.`,
        variant: "success",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/employees/me/dependents"],
      });
      setPendingDeletionDependent(null);
      setDeleteDependentError(null);
    },
    onError: (error) => {
      const message = parseServerErrorMessage(
        error,
        "Failed to delete dependent. Please try again."
      );
      setDeleteDependentError(message);
      toast({
        title: "Unable to delete dependent",
        description: message,
        variant: "destructive",
      });
    },
  });

  const updateEmployeeMutation = useMutation({
    mutationFn: async (payload: any) => {
      const response = await apiRequest("PATCH", "/api/employees/me", payload);
      return response.json();
    },
    onSuccess: async (updatedEmployee) => {
      toast({
        title: "Profile Updated",
        description: "Your profile information has been saved successfully.",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/employees/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      setIsEditing(false);

      // Log to history
      try {
        const changes = (window as any).__profileChanges || {};
        if (employeeRecord?.id && Object.keys(changes).length > 0) {
          await apiRequest("POST", `/api/employees/${employeeRecord.id}/history`, {
            action: "profile_updated",
            description: "Employee updated their profile",
            recordType: "employee",
            recordId: employeeRecord.id,
            changes,
          });
          delete (window as any).__profileChanges;
        }
      } catch (err) {
        console.error("Failed to create profile update history:", err);
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  });

  // Auto-save mutation (doesn't exit edit mode, updates save indicator)
  const autoSaveMutation = useMutation({
    mutationFn: async (payload: any) => {
      const response = await apiRequest("PATCH", "/api/employees/me", payload);
      return response.json();
    },
    onSuccess: async (updatedEmployee) => {
      // Update save status to "saved"
      saveStatus.setSaved();

      // Update the last saved data reference
      const data = getValues();
      lastSavedDataRef.current = JSON.stringify(data);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["/api/employees/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });

      // Log to history (silently, no toast for auto-save)
      try {
        const changes = (window as any).__profileChanges || {};
        if (employeeRecord?.id && Object.keys(changes).length > 0) {
          await apiRequest("POST", `/api/employees/${employeeRecord.id}/history`, {
            action: "profile_updated",
            description: "Employee updated their profile (auto-save)",
            recordType: "employee",
            recordId: employeeRecord.id,
            changes,
          });
          delete (window as any).__profileChanges;
        }
      } catch (err) {
        console.error("Failed to create profile update history:", err);
      }
    },
    onError: (error: Error) => {
      // Update save status to "error"
      saveStatus.setError(error.message || "Failed to save");
    },
    // Retry mechanism for auto-save
    retry: 2,
    retryDelay: 1000,
  });

  // Add emergency contact mutation
  const addEmergencyContactMutation = useMutation({
    mutationFn: async (contactData: any) => {
      const response = await apiRequest("POST", "/api/employees/me/emergency-contacts", contactData);
      return response.json();
    },
    onSuccess: async (created: any) => {
      toast({
        title: "Emergency Contact Added",
        description: "New emergency contact has been added successfully.",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/employees/me/emergency-contacts"] });
      setShowEmergencyContactDialog(false);
      setEditingEmergencyContact(null);
      
      // Log to history
      try {
        const contactName = created?.name || (window as any).__emergencyContactAddName || "New Contact";
        if (employeeRecord?.id) {
          await apiRequest("POST", `/api/employees/${employeeRecord.id}/history`, {
            action: "emergency_contact_added",
            description: `Added emergency contact: ${contactName}`,
            recordType: "emergency_contact",
            recordId: created?.id ?? null,
          });
          delete (window as any).__emergencyContactAddName;
        }
      } catch (err) {
        console.error("Failed to create emergency contact history:", err);
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add emergency contact. Please try again.",
        variant: "destructive",
      });
    }
  });

  // Update emergency contact mutation
  const updateEmergencyContactMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await apiRequest("PATCH", `/api/emergency-contacts/${id}`, data);
      return response.json();
    },
    onSuccess: async (updated: any) => {
      toast({
        title: "Emergency Contact Updated",
        description: "Emergency contact has been updated successfully.",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/employees/me/emergency-contacts"] });
      setEditingEmergencyContact(null);
      setShowEmergencyContactDialog(false);
      
      // Log to history
      try {
        const changes = (window as any).__emergencyContactChanges || {};
        const contactName = (window as any).__emergencyContactUpdateName || updated?.name || "Emergency Contact";
        if (employeeRecord?.id) {
          await apiRequest("POST", `/api/employees/${employeeRecord.id}/history`, {
            action: "emergency_contact_updated",
            description: `Updated emergency contact: ${contactName}`,
            recordType: "emergency_contact",
            recordId: updated?.id ?? null,
            changes,
          });
          delete (window as any).__emergencyContactChanges;
          delete (window as any).__emergencyContactUpdateName;
        }
      } catch (err) {
        console.error("Failed to create emergency contact update history:", err);
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update emergency contact. Please try again.",
        variant: "destructive",
      });
    }
  });

  // Delete emergency contact mutation
  const deleteEmergencyContactMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest("DELETE", `/api/emergency-contacts/${id}`);
    },
    onSuccess: () => {
      toast({
        title: "Emergency Contact Deleted",
        description: "Emergency contact has been removed successfully.",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/employees/me/emergency-contacts"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete emergency contact. Please try again.",
        variant: "destructive",
      });
    }
  });

  const handleSaveProfile = handleSubmit((data) => {
    const addressLine = composeAddressLine(data);

    const payload: any = {
      firstName: data.firstName,
      lastName: data.lastName,
      personalEmail: data.personalEmail,
      ssn: data.ssn || null,
      dateOfBirth: data.dateOfBirth || null,
      sex: data.sex || null,
      maritalStatus: data.maritalStatus || null,
      phone: data.phoneNumber || null,
      address: addressLine || null,
      address1: data.address1 || null,
      address2: data.address2 || null,
      city: data.city || null,
      state: data.state || null,
      zipCode: data.zipCode || null,
      county: data.county || null,
      country: data.country || null,
    };
    // Unchanged (masked or same as revealed) SSN is not sent, so it can't overwrite the stored one.
    if (ssnToSave(payload.ssn, revealedSsn) === undefined) delete payload.ssn;

    // Compute field-level diffs for history
    const changes: Record<string, { from: any; to: any }> = {};
    const fieldsToTrack = [
      'firstName', 'lastName', 'personalEmail', 'phone', 'dateOfBirth',
      'sex', 'maritalStatus', 'ssn',
      'address1', 'address2', 'city', 'state', 'zipCode', 'county', 'country'
    ];
    fieldsToTrack.forEach((field) => {
      if (field === 'ssn' && !('ssn' in payload)) return;
      const oldVal = (employeeRecord as any)?.[field];
      const newVal = field === 'phone' ? payload.phone : payload[field];
      if (oldVal !== newVal && (oldVal || newVal)) {
        changes[field] = { from: oldVal ?? 'N/A', to: newVal ?? 'N/A' };
      }
    });
    (window as any).__profileChanges = changes;

    updateEmployeeMutation.mutate(payload);
  });

  const handleAddDependent = () => {
    if (!newDependent.firstName || !newDependent.lastName || !newDependent.relationship || !newDependent.dateOfBirth) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }
    addDependentMutation.mutate(newDependent);
  };

  const handleEditDependent = (dependent: any) => {
    setEditingDependent(dependent);
    setRevealedDependentSsn(undefined);
    setNewDependent({
      firstName: dependent.firstName,
      lastName: dependent.lastName,
      relationship: dependent.relationship,
      dateOfBirth: dependent.dateOfBirth ? new Date(dependent.dateOfBirth).toISOString().split("T")[0] : "",
      ssn: dependent.ssn || "",
      sex: dependent.sex || "",
      isStudent: dependent.isStudent || false
    });
    setShowDependentDialog(true);
  };

  const handleUpdateDependent = () => {
    if (!newDependent.firstName || !newDependent.lastName || !newDependent.relationship || !newDependent.dateOfBirth) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }
    if (editingDependent && typeof editingDependent.id === 'number') {
      // Unchanged (masked or same as revealed) SSN is not sent, so it can't overwrite the stored one.
      const payload: Partial<typeof newDependent> = { ...newDependent };
      if (ssnToSave(payload.ssn, revealedDependentSsn) === undefined) delete payload.ssn;
      // Compute changes for history
      const changes: Record<string, { from: any; to: any }> = {};
      const fieldsToTrack = ['firstName', 'lastName', 'relationship', 'dateOfBirth', 'sex', 'ssn'];
      fieldsToTrack.forEach((field) => {
        if (field === 'ssn' && !('ssn' in payload)) return;
        let oldVal = (editingDependent as any)[field];
        let newVal = (payload as any)[field];
        
        // Normalize date format for comparison
        if (field === 'dateOfBirth' && oldVal && newVal) {
          oldVal = typeof oldVal === 'string' ? oldVal.split('T')[0] : oldVal;
          newVal = typeof newVal === 'string' ? newVal.split('T')[0] : newVal;
        }
        
        if (oldVal !== newVal && (oldVal || newVal)) {
          changes[field] = { from: oldVal ?? 'N/A', to: newVal ?? 'N/A' };
        }
      });
      (window as any).__dependentChanges = changes;
      (window as any).__dependentName = `${newDependent.firstName || editingDependent.firstName || ''} ${newDependent.lastName || editingDependent.lastName || ''}`.trim();
      
      updateDependentMutation.mutate({
        id: editingDependent.id as number,
        data: payload
      });
    }
  };

  const handleRequestDeleteDependent = useCallback((dependent: Dependent) => {
    setDeleteDependentError(null);
    setPendingDeletionDependent(dependent);
  }, []);

  const handleCloseDependentDeletion = useCallback(() => {
    setPendingDeletionDependent(null);
    setDeleteDependentError(null);
  }, []);

  const handleConfirmDependentDeletion = useCallback(async () => {
    if (!pendingDeletionDependent?.id) {
      return;
    }
    try {
      await deleteDependentMutation.mutateAsync(
        pendingDeletionDependent.id as number
      );
    } catch {
      // Error state handled via onError callback on the mutation.
    }
  }, [pendingDeletionDependent?.id, deleteDependentMutation]);

  const handleNavigateToEnrollments = useCallback(() => {
    setLocation("/benefits?role=employee");
  }, [setLocation]);

  const handleDismissBanner = () => {
    // Set cookie to expire in 1 year
    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    document.cookie = `profileBannerDismissed=true; expires=${expiryDate.toUTCString()}; path=/`;
    setShowBanner(false);
  };

  const handleCloseDialog = () => {
    setShowDependentDialog(false);
    setEditingDependent(null);
    setNewDependent({
      firstName: "",
      lastName: "",
      relationship: "",
      dateOfBirth: "",
      ssn: "",
      sex: "",
      isStudent: false
    });
  };

  const handleEmergencyContactSubmit = (data: any) => {
    if (editingEmergencyContact && editingEmergencyContact.id) {
      // Compute changes for history
      const changes: Record<string, { from: any; to: any }> = {};
      const fieldsToTrack = [
        'name', 'phone', 'email', 'relationship', 'contactType',
        'address1', 'address2', 'city', 'state', 'zipCode', 'county', 'country'
      ];
      fieldsToTrack.forEach((field) => {
        const oldVal = (editingEmergencyContact as any)[field];
        const newVal = data[field];
        if (oldVal !== newVal && (oldVal || newVal)) {
          changes[field] = { from: oldVal ?? 'N/A', to: newVal ?? 'N/A' };
        }
      });
      (window as any).__emergencyContactChanges = changes;
      (window as any).__emergencyContactUpdateName = data.name;
      
      updateEmergencyContactMutation.mutate({
        id: editingEmergencyContact.id,
        data,
      });
    } else {
      (window as any).__emergencyContactAddName = data.name;
      addEmergencyContactMutation.mutate(data);
    }
  };

  const handleEditEmergencyContact = (contact: any) => {
    setEditingEmergencyContact(contact);
    setShowEmergencyContactDialog(true);
  };

  const handleDeleteEmergencyContact = (id: number) => {
    if (confirm("Are you sure you want to delete this emergency contact?")) {
      deleteEmergencyContactMutation.mutate(id);
    }
  };

  return (
    <Form {...form}>
      <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold">My Profile</h1>
          {/* Auto-save indicator - only shown when editing */}
          {isEditing && (
            <SaveIndicator
              status={saveStatus.status}
              errorMessage={saveStatus.errorMessage}
              onDismiss={saveStatus.setIdle}
              autoDismissDelay={3000}
            />
          )}
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => {
                // Reset save status when canceling
                saveStatus.reset();
                setIsEditing(false);
              }}>
                Cancel
              </Button>
              <Button onClick={handleSaveProfile}>
                <Save className="h-4 w-4 mr-2" />
                Save & Exit
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Profile
            </Button>
          )}
        </div>
      </div>

      {/* Important Notice */}
      {showBanner && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <div className="font-medium text-yellow-900">Profile Accuracy Important</div>
                  <div className="text-sm text-yellow-700 mt-1">
                    Please ensure all information is accurate and up-to-date. This information is used for benefits enrollment,
                    tax reporting, and emergency situations. Contact HR if you need assistance updating any fields.
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismissBanner}
                className="text-yellow-600 hover:text-yellow-900 hover:bg-yellow-100"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className={errors.firstName ? "text-red-600" : ""}>
                  First Name *
                </Label>
                {isEditing ? (
                  <>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="firstName"
                          {...field}
                          className={errors.firstName ? "border-red-500 focus:ring-red-500" : ""}
                        />
                      )}
                    />
                    {errors.firstName && (
                      <p className="text-xs text-red-600 mt-1">{errors.firstName.message}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm mt-2">{employeeRecord?.firstName || '-'}</p>
                )}
              </div>
              <div>
                <Label htmlFor="lastName" className={errors.lastName ? "text-red-600" : ""}>
                  Last Name *
                </Label>
                {isEditing ? (
                  <>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="lastName"
                          {...field}
                          className={errors.lastName ? "border-red-500 focus:ring-red-500" : ""}
                        />
                      )}
                    />
                    {errors.lastName && (
                      <p className="text-xs text-red-600 mt-1">{errors.lastName.message}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm mt-2">{employeeRecord?.lastName || '-'}</p>
                )}
              </div>

              <div>
                <Label htmlFor="ssn" className={`flex items-center gap-2 ${errors.ssn ? "text-red-600" : ""}`}>
                  <Shield className="h-4 w-4" />
                  Social Security Number *
                </Label>
                {isEditing ? (
                  <>
                    <Controller
                      name="ssn"
                      control={control}
                      render={({ field }) => (
                        <SsnInput
                          id="ssn"
                          employeeId={employeeRecord?.id}
                          value={field.value || ""}
                          onChange={field.onChange}
                          onRevealed={setRevealedSsn}
                          className={errors.ssn ? "border-red-500 focus:ring-red-500" : ""}
                        />
                      )}
                    />
                    {errors.ssn && (
                      <p className="text-xs text-red-600 mt-1">{errors.ssn.message}</p>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2 mt-2">
                    <p className="text-sm">
                      {employeeRecord?.ssn
                        ? showSSN && revealedSsn
                          ? formatSSN(revealedSsn)
                          : maskSSN(employeeRecord.ssn)
                        : '-'}
                    </p>
                    {employeeRecord?.ssn && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={toggleSSNReveal}
                      >
                        {showSSN ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                {isEditing ? (
                  <Controller
                    name="dateOfBirth"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="dateOfBirth"
                        type="date"
                        {...field}
                      />
                    )}
                  />
                ) : (
                  <p className="text-sm mt-2">{employeeRecord?.dateOfBirth ? formatDateDisplay(employeeRecord.dateOfBirth) : '-'}</p>
                )}
              </div>
              <div>
                <Label htmlFor="sex">Sex *</Label>
                {isEditing ? (
                  <Controller
                    name="sex"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value || ""}
                        onValueChange={(value) => {
                          field.onChange(value);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select sex" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                ) : (
                  <p className="text-sm mt-2">{capitalizeFirst(employeeRecord?.sex) || '-'}</p>
                )}
              </div>
              <div>
                <Label htmlFor="maritalStatus">Marital Status</Label>
                {isEditing ? (
                  <Controller
                    name="maritalStatus"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value || ""}
                        onValueChange={(value) => {
                          field.onChange(value);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Single</SelectItem>
                          <SelectItem value="married">Married</SelectItem>
                          <SelectItem value="divorced">Divorced</SelectItem>
                          <SelectItem value="widowed">Widowed</SelectItem>
                          <SelectItem value="separated">Separated</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                ) : (
                  <p className="text-sm mt-2">{capitalizeFirst(employeeRecord?.maritalStatus) || '-'}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-blue-600" />
                  Work Email
                </Label>
                <p className="text-sm mt-2 text-muted-foreground">{employeeRecord?.email || '-'}</p>
                <p className="text-xs text-muted-foreground mt-1">Set by your employer</p>
              </div>
              <div>
                <Label htmlFor="workPhone" className="flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5 text-blue-600" />
                  Work Phone
                </Label>
                <p className="text-sm mt-2 text-muted-foreground">{employeeRecord?.workPhone || '-'}</p>
                <p className="text-xs text-muted-foreground mt-1">Set by your employer</p>
              </div>
            </div>
            
            <Separator className="my-4" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="personalEmail" className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  Personal Email
                </Label>
                {isEditing ? (
                  <Controller
                    name="personalEmail"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="personalEmail"
                        type="email"
                        {...field}
                        placeholder="your.email@example.com"
                      />
                    )}
                  />
                ) : (
                  <p className="text-sm mt-2">{employeeRecord?.personalEmail || employeeRecord?.email || '-'}</p>
                )}
              </div>
              <div>
                <Label htmlFor="phoneNumber" className="flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" />
                  Personal Phone *
                </Label>
                {isEditing ? (
                  <Controller
                    name="phoneNumber"
                    control={control}
                    render={({ field }) => {
                      const handlePrimaryPhoneChange = (
                        event: ChangeEvent<HTMLInputElement>
                      ) => {
                        const formattedValue = formatPhoneInputValue(
                          event.target.value
                        );

                        field.onChange(formattedValue);
                      };

                      return (
                        <Input
                          id="phoneNumber"
                          {...field}
                          value={field.value || ""}
                          placeholder="(555) 123-4567"
                          onChange={handlePrimaryPhoneChange}
                        />
                      );
                    }}
                  />
                ) : (
                  <p className="text-sm mt-2">{employeeRecord?.phone || '-'}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Address Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Residential Address
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isEditing ? (
            <AddressFields control={form.control} />
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Address 1</Label>
                  <p className="text-sm mt-2">{employeeRecord?.address1 || '-'}</p>
                </div>
                <div>
                  <Label>Address 2</Label>
                  <p className="text-sm mt-2">{employeeRecord?.address2 || '-'}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>City</Label>
                  <p className="text-sm mt-2">{employeeRecord?.city || '-'}</p>
                </div>
                <div>
                  <Label>State</Label>
                  <p className="text-sm mt-2">{employeeRecord?.state || '-'}</p>
                </div>
                <div>
                  <Label>Zip</Label>
                  <p className="text-sm mt-2">{employeeRecord?.zipCode || '-'}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>County</Label>
                  <p className="text-sm mt-2">{employeeRecord?.county || '-'}</p>
                </div>
                <div>
                  <Label>Country</Label>
                  <p className="text-sm mt-2">{employeeRecord?.country || '-'}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dependents */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Dependents
            </CardTitle>
            <Dialog open={showDependentDialog} onOpenChange={setShowDependentDialog}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Dependent
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingDependent ? 'Edit Dependent' : 'Add New Dependent'}</DialogTitle>
                </DialogHeader>
                {!editingDependent ? (
                  <DependentQuickAddForm
                    submitLabel="Add Dependent"
                    onCancel={handleCloseDialog}
                    onSaved={async (created) => {
                      setShowDependentDialog(false);
                      try {
                        if (employeeRecord?.id) {
                          await apiRequest(
                            "POST",
                            `/api/employees/${employeeRecord.id}/history`,
                            {
                              action: "dependent_added",
                              description: `Added dependent: ${created?.firstName ?? ""} ${created?.lastName ?? ""}`.trim(),
                              recordType: "dependent",
                              recordId: created?.id ?? null,
                            }
                          );
                        }
                      } catch (err) {
                        console.error("Failed to create dependent history:", err);
                      }
                    }}
                  />
                ) : (
                <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="depFirstName">First Name *</Label>
                    <Input 
                      id="depFirstName" 
                      value={newDependent.firstName}
                      onChange={(e) => setNewDependent(prev => ({ ...prev, firstName: e.target.value }))}
                      placeholder="Enter first name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="depLastName">Last Name *</Label>
                    <Input 
                      id="depLastName" 
                      value={newDependent.lastName}
                      onChange={(e) => setNewDependent(prev => ({ ...prev, lastName: e.target.value }))}
                      placeholder="Enter last name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="depRelationship">Relationship *</Label>
                    <Select 
                      value={newDependent.relationship} 
                      onValueChange={(value) => setNewDependent(prev => ({ ...prev, relationship: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEPENDENT_RELATIONSHIP_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="depDateOfBirth">Date of Birth *</Label>
                    <Input 
                      id="depDateOfBirth" 
                      type="date"
                      value={newDependent.dateOfBirth}
                      onChange={(e) => setNewDependent(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="depSsn">Social Security Number</Label>
                    <SsnInput
                      id="depSsn"
                      employeeId={employeeRecord?.id}
                      dependentId={editingDependent.id}
                      value={newDependent.ssn || ""}
                      onChange={(v) => setNewDependent(prev => ({ ...prev, ssn: v }))}
                      onRevealed={setRevealedDependentSsn}
                    />
                  </div>
                  <div>
                    <Label htmlFor="depSex">Sex</Label>
                    <Select 
                      value={newDependent.sex} 
                      onValueChange={(value) => setNewDependent(prev => ({ ...prev, sex: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sex" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end space-x-2 mt-4">
                  <Button variant="outline" onClick={handleCloseDialog}>
                    Cancel
                  </Button>
                  <Button onClick={editingDependent ? handleUpdateDependent : handleAddDependent}>
                    {editingDependent ? 'Update Dependent' : 'Add Dependent'}
                  </Button>
                </div>
                </>
                )}
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dependentsLoading && <p className="text-sm text-muted-foreground">Loading dependents...</p>}
              {!dependentsLoading &&
                dependents.map((dependent) => {
                  const dateOfBirthLabel = dependent.dateOfBirth
                    ? formatDateDisplay(dependent.dateOfBirth)
                    : "";
                  const sexLabel = dependent.sex ? `• ${dependent.sex}` : "";
                  const relationshipLabel = dependent.relationship ?? "Dependent";
                  const metadataParts = [
                    relationshipLabel,
                    dateOfBirthLabel ? `• Born ${dateOfBirthLabel}` : "",
                    sexLabel,
                  ].filter(Boolean);
                  const metadata = metadataParts.join(" ");
                  const fullName = `${dependent.firstName ?? ""} ${
                    dependent.lastName ?? ""
                  }`.trim() || relationshipLabel;

                  const handleEditClick = () => handleEditDependent(dependent);
                  const handleDeleteClick = () =>
                    handleRequestDeleteDependent(dependent);

                  return (
                    <div
                      key={dependent.id}
                      className="flex items-center justify-between rounded-lg border p-3"
                    >
                      <div>
                        <div className="font-medium">{fullName}</div>
                        <div className="text-sm text-muted-foreground">
                          {metadata}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="capitalize">
                          {relationshipLabel}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleEditClick}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleDeleteClick}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              {!dependentsLoading && dependents.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No dependents added yet. Click "Add Dependent" to get started.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contacts */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5" />
              Emergency Contacts
            </CardTitle>
            <Button
              size="sm"
              onClick={() => {
                setEditingEmergencyContact(null);
                setShowEmergencyContactDialog(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Contact
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {emergencyLoading && <p className="text-sm text-muted-foreground">Loading emergency contacts...</p>}
              {!emergencyLoading && emergencyContacts.map((contact: any) => (
                <div key={contact.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {contact.name}
                      {contact.isPrimary && <Badge variant="default">Primary</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground">{contact.relationship} • {contact.phone}</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="capitalize">{contact.relationship}</Badge>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleEditEmergencyContact(contact)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteEmergencyContact(contact.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {!emergencyLoading && emergencyContacts.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No emergency contacts added yet. Click "Add Contact" to get started.</p>
              )}
            </div>
          </CardContent>
          <EmergencyContactModal
            open={showEmergencyContactDialog}
            onOpenChange={(open) => {
              setShowEmergencyContactDialog(open);
              if (!open) {
                setEditingEmergencyContact(null);
              }
            }}
            onSubmit={handleEmergencyContactSubmit}
            editingContact={editingEmergencyContact}
            employeeName={
              employeeRecord
                ? `${employeeRecord.firstName} ${employeeRecord.lastName}`
                : undefined
            }
            employeeAddress={
              employeeRecord
                ? {
                    address1: employeeRecord.address1 || "",
                    address2: employeeRecord.address2 || "",
                    city: employeeRecord.city || "",
                    state: employeeRecord.state || "",
                    zipCode: employeeRecord.zipCode || "",
                    county: employeeRecord.county || "",
                    country: employeeRecord.country || "United States",
                  }
                : undefined
            }
            isPending={
              addEmergencyContactMutation.isPending ||
              updateEmergencyContactMutation.isPending
            }
          />
        </Card>
      </div>

    </div>
    <DependentDeletionDialog
      dependent={pendingDeletionDependent as any}
      isOpen={Boolean(pendingDeletionDependent)}
      onClose={handleCloseDependentDeletion}
      onConfirm={handleConfirmDependentDeletion}
      onNavigateToEnrollments={handleNavigateToEnrollments}
      isProcessing={deleteDependentMutation.isPending}
      errorMessage={deleteDependentError}
    />
    </Form>
  );
}
